import type { HsGeometry } from '../../graphic-engine-new/geometry/geometry.interface';
import type { HsGraphics } from '../../graphic-render-new/graphics.class';
import { HsIndexedGeometryData } from '../../tools-new/geometry-utils/indexed-geometry-data.class';
import { hsMakeGeometry } from '../../tools-new/geometry-utils/make-geometry.function';
import { hsMakeIndexedGeometry } from '../../tools-new/geometry-utils/make-indexed-geometry.function';
import { hsMakeTexturedIndexedGeometry } from '../../tools-new/geometry-utils/make-textured-indexed-geometry.function';
import { HsRegularGeometryData } from '../../tools-new/geometry-utils/regular-geometry-data.class';
import type { HsDecodedMesh, HsNumericArray } from '../data/decoded-geometry.interface';

/** Why a decoded mesh produced no geometry. */
export type HsSceneMeshRejection = 'invalidVertices' | 'emptyIndices' | 'invalidIndices' | 'regularFallbackFailed';

export interface HsSceneMeshGeometryResult {
  geometry: HsGeometry | null;
  rejection: HsSceneMeshRejection | null;
  /** The indexed build failed; `geometry` is then the non-indexed fallback, if that worked. */
  indexedBuildFailed: boolean;
}

export interface HsSceneMeshGeometryOptions {
  /** Build a textured geometry from the mesh UVs; throws when they are missing. */
  textured: boolean;
  /** Prepare the indexed geometry for instanced drawing. */
  instancing: boolean;
}

/** The values, or a copy with non-finite values replaced by 0. */
export function hsSanitizeFiniteValues(values: HsNumericArray): HsNumericArray {
  for (let index: number = 0; index < values.length; index++) {
    if (!Number.isFinite(values[index])) {
      return Array.from(values, (value: number) => (Number.isFinite(value) ? value : 0));
    }
  }
  return values;
}

/**
 * GPU geometry for a decoded BIM mesh. Repairs what the payloads get wrong: non-finite values, missing
 * normals and indices, 1-based indices, trailing partial triangles and out-of-range triangles. When the
 * indexed build fails, an untextured mesh falls back to a non-indexed geometry.
 */
export function hsBuildSceneMeshGeometry(
  graphics: HsGraphics,
  mesh: HsDecodedMesh,
  options: HsSceneMeshGeometryOptions
): HsSceneMeshGeometryResult {
  if (mesh.vertices.length === 0 || mesh.vertices.length % 3 !== 0) {
    return { geometry: null, rejection: 'invalidVertices', indexedBuildFailed: false };
  }
  const vertexCount: number = mesh.vertices.length / 3;
  const vertices: HsNumericArray = hsSanitizeFiniteValues(mesh.vertices);
  const normals: HsNumericArray =
    mesh.normals.length === mesh.vertices.length
      ? hsSanitizeFiniteValues(mesh.normals)
      : new Array<number>(mesh.vertices.length).fill(0);

  const indices: HsNumericArray = mesh.indices.length === 0 ? sequentialIndices(vertexCount) : mesh.indices;
  const sanitizedIndices: number[] | null = hsRepairTriangleIndices(indices, vertexCount);
  if (sanitizedIndices === null) {
    return { geometry: null, rejection: 'emptyIndices', indexedBuildFailed: false };
  }
  if (sanitizedIndices.length === 0) {
    return { geometry: null, rejection: 'invalidIndices', indexedBuildFailed: false };
  }

  if (options.textured && mesh.uvs?.length !== vertexCount * 2) {
    throw new Error(`Textured mesh ${mesh.geometryId} has missing or invalid UV coordinates.`);
  }
  const geometryData: HsIndexedGeometryData = new HsIndexedGeometryData(
    vertices,
    normals,
    sanitizedIndices,
    options.textured ? mesh.uvs : undefined
  );
  const indexed: HsGeometry | null = options.textured
    ? hsMakeTexturedIndexedGeometry(graphics, geometryData)
    : hsMakeIndexedGeometry(graphics, geometryData, options.instancing);
  if (indexed !== null) {
    return { geometry: indexed, rejection: null, indexedBuildFailed: false };
  }
  if (options.textured) {
    throw new Error(`Unable to create textured geometry ${mesh.geometryId}.`);
  }

  const expandedVertices: number[] = [];
  const expandedNormals: number[] = [];
  sanitizedIndices.forEach((vertexIndex: number) => {
    const baseIndex: number = vertexIndex * 3;
    expandedVertices.push(vertices[baseIndex] ?? 0, vertices[baseIndex + 1] ?? 0, vertices[baseIndex + 2] ?? 0);
    expandedNormals.push(normals[baseIndex] ?? 0, normals[baseIndex + 1] ?? 0, normals[baseIndex + 2] ?? 0);
  });
  if (expandedVertices.length === 0 || expandedNormals.length !== expandedVertices.length) {
    return { geometry: null, rejection: 'regularFallbackFailed', indexedBuildFailed: true };
  }
  const fallback: HsGeometry | null = hsMakeGeometry(
    graphics,
    new HsRegularGeometryData(expandedVertices, expandedNormals)
  );
  return fallback === null
    ? { geometry: null, rejection: 'regularFallbackFailed', indexedBuildFailed: true }
    : { geometry: fallback, rejection: null, indexedBuildFailed: true };
}

function sequentialIndices(count: number): number[] {
  const indices: number[] = new Array<number>(count);
  for (let i: number = 0; i < count; i++) {
    indices[i] = i;
  }
  return indices;
}

/**
 * Triangle indices that are safe to draw: 1-based indices shifted to 0-based, a trailing partial
 * triangle dropped, and triangles with non-integer or out-of-range indices removed. Null when nothing
 * is left after dropping the partial triangle; an empty array when every triangle was invalid.
 */
export function hsRepairTriangleIndices(source: HsNumericArray, vertexCount: number): number[] | null {
  let indices: HsNumericArray = source;
  // Some payloads store mesh indices in 1-based form.
  let minIndex: number = Number.POSITIVE_INFINITY;
  let maxIndexValue: number = Number.NEGATIVE_INFINITY;
  for (let i: number = 0; i < indices.length; i++) {
    const indexValue: number = indices[i];
    if (!Number.isInteger(indexValue)) {
      continue;
    }
    minIndex = Math.min(minIndex, indexValue);
    maxIndexValue = Math.max(maxIndexValue, indexValue);
  }
  if (minIndex >= 1 && maxIndexValue <= vertexCount) {
    indices = Array.from(indices, (indexValue: number) => indexValue - 1);
  }
  if (indices.length % 3 !== 0) {
    indices = Array.from(indices).slice(0, indices.length - (indices.length % 3));
  }
  if (indices.length === 0) {
    return null;
  }
  const maxIndex: number = vertexCount - 1;
  const valid = (index: number): boolean => Number.isInteger(index) && index >= 0 && index <= maxIndex;
  const repaired: number[] = [];
  for (let i: number = 0; i < indices.length; i += 3) {
    if (valid(indices[i]) && valid(indices[i + 1]) && valid(indices[i + 2])) {
      repaired.push(indices[i], indices[i + 1], indices[i + 2]);
    }
  }
  return repaired;
}
